<template>
  <div class="login-container">
    <MessageBox
      :visible="showMessage"
      :message="messageText"
      :type="messageType"
    />
    <div class="login-card">
      <h2 class="login-title">用户登录</h2>

      <div class="tab-container">
        <button 
          :class="['login-tab', { 'login-tab--active': activeTab === 'account' }]"
          @click="activeTab = 'account'">
          账号/手机/邮箱登录
        </button>
      </div>

      <transition name="fade" mode="out-in">
        <form @submit.prevent="handleSubmit" :key="activeTab" class="login-form">
          <!-- 登录表单 -->
          <div v-if="activeTab === 'account'" class="form-section">
            <div class="input-group">
              <label>账号/手机号/邮箱</label>
              <input
                v-model="form.account.identifier"
                type="text"
                :class="{ 'input-error': errors.account.identifier }"
                placeholder="请输入账号、手机号或邮箱"
              >
              <p v-if="errors.account.identifier" class="error-msg">{{ errors.account.identifier }}</p>
            </div>
            <div class="input-group">
              <label>密码</label>
              <input
                v-model="form.account.password"
                type="password"
                :class="{ 'input-error': errors.account.password }"
                placeholder="请输入密码"
              >
              <p v-if="errors.account.password" class="error-msg">{{ errors.account.password }}</p>
            </div>
            
            <div style="display: flex; justify-content: space-between; align-items: center; margin: 24px 0; padding: 0 24px;">
              <router-link 
                to="/register" 
                style="font-size: 14px; color: #666; text-decoration: none; transition: color 0.3s;">
                <span style="vertical-align: middle;">没有账号？</span>
                <span style="color: #0052CC; margin-left: 8px; font-weight: 500;">立即注册</span>
              </router-link>
              <a 
                href="javascript:;" 
                @click="activeTab = 'forgot'"
                style="font-size: 14px; color: #0052CC; text-decoration: none; transition: color 0.3s;">
                忘记密码？
              </a>
            </div>
          </div>

          <!-- 忘记密码表单 -->
          <div v-if="activeTab === 'forgot'" class="form-section mt-6">
            <div class="text-center mb-6">
              <h3 class="text-lg font-semibold text-gray-800">重置密码</h3>
              <p class="text-sm text-gray-600 mt-1">请输入注册邮箱</p>
            </div>
            <div class="input-group">
              <label>邮箱</label>
              <div class="verification-code-group">
                <input
                  v-model="form.forgot.email"
                  type="email"
                  :class="{ 'input-error': errors.forgot.email }"
                  placeholder="请输入注册邮箱"
                  class="verification-input"
                >
                <button 
                  type="button"
                  @click="handleSendResetCode"
                  :disabled="isSendingCode || !form.forgot.email || countdown > 0"
                  class="send-code-btn"
                >
                  {{ isSendingCode ? '发送中...' : countdown > 0 ? `${countdown}秒后重试` : '获取验证码' }}
                </button>
              </div>
              <p v-if="errors.forgot.email" class="error-msg">{{ errors.forgot.email }}</p>
            </div>
            <div class="input-group">
              <label>验证码</label>
              <input
                v-model="form.forgot.code"
                type="text"
                :class="{ 'input-error': errors.forgot.code }"
                placeholder="请输入验证码"
              >
              <p v-if="errors.forgot.code" class="error-msg">{{ errors.forgot.code }}</p>
            </div>
            <div class="input-group">
              <label>新密码</label>
              <div class="password-input-group">
                <input
                  v-model="form.forgot.newPassword"
                  :type="showNewPassword ? 'text' : 'password'"
                  :class="{ 'input-error': errors.forgot.newPassword }"
                  placeholder="请输入新密码（8-20位，含大小写字母和数字）"
                >
                <button 
                  type="button"
                  class="toggle-password-btn"
                  @click="showNewPassword = !showNewPassword"
                >
                  <i :class="showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
              <p v-if="errors.forgot.newPassword" class="error-msg">{{ errors.forgot.newPassword }}</p>
            </div>
            <div class="input-group">
              <label>确认新密码</label>
              <div class="password-input-group">
                <input
                  v-model="form.forgot.confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  :class="{ 'input-error': errors.forgot.confirmPassword }"
                  placeholder="请再次输入新密码"
                >
                <button 
                  type="button"
                  class="toggle-password-btn"
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
              <p v-if="errors.forgot.confirmPassword" class="error-msg">{{ errors.forgot.confirmPassword }}</p>
            </div>
          </div>

          <button 
            type="submit"
            class="submit-btn"
            :disabled="isSubmitting">
            {{ isSubmitting ? '提交中...' : '立即提交' }}
          </button>
        </form>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import MessageBox from '@/components/MessageBox.vue';

export default {
  components: {
    MessageBox
  },
  data() {
    return {
      activeTab: 'account',
      form: {
        account: { identifier: '', password: '' },
        forgot: { 
          email: '', 
          code: '', 
          newPassword: '',
          confirmPassword: ''
        }
      },
      errors: {
        account: { identifier: '', password: '' },
        forgot: { 
          email: '', 
          code: '', 
          newPassword: '',
          confirmPassword: ''
        }
      },
      countdown: 0,
      isSubmitting: false,
      isSendingCode: false,
      showMessage: false,
      messageText: '',
      messageType: 'error',
      showNewPassword: false,
      showConfirmPassword: false,
    }
  },
  methods: {
    ...mapActions('user', ['login']),
    
    showMessageBox(message, type = 'error') {
      this.messageText = message
      this.messageType = type
      this.showMessage = true
      setTimeout(() => {
        this.showMessage = false
      }, 3000)
    },
    
    validateForm() {
      this.errors = {
        account: { identifier: '', password: '' },
        forgot: { 
          email: '', 
          code: '', 
          newPassword: '',
          confirmPassword: ''
        }
      }
      
      let isValid = true
      
      if (this.activeTab === 'account') {
        const identifier = this.form.account.identifier.trim();
        if (!identifier) {
          this.showMessageBox('请输入账号、手机号或邮箱');
          isValid = false;
        } else if (!/^(1[3-9]\d{9}|[\w-]+(\.[\w-]+)*@[\w-]+(\.[\w-]+)+|[\u4e00-\u9fa5\w]{4,20})$/.test(identifier)) {
          this.showMessageBox('格式不正确，请输入有效的账号、手机号或邮箱');
          isValid = false;
        }
        if (!this.form.account.password.trim()) {
          this.showMessageBox('请输入密码')
          isValid = false
        }
      }
      
      if (this.activeTab === 'forgot') {
        const email = this.form.forgot.email.trim();
        if (!email) {
          this.showMessageBox('请输入邮箱');
          isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          this.showMessageBox('请输入有效的邮箱地址');
          isValid = false;
        }

        if (!this.form.forgot.code.trim()) {
          this.showMessageBox('请输入验证码');
          isValid = false;
        }

        if (!this.form.forgot.newPassword) {
          this.showMessageBox('请输入新密码');
          isValid = false;
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[\w\W]{8,20}$/.test(this.form.forgot.newPassword)) {
          this.showMessageBox('密码需8-20位，包含大小写字母和数字');
          isValid = false;
        }

        if (!this.form.forgot.confirmPassword) {
          this.showMessageBox('请确认新密码');
          isValid = false;
        } else if (this.form.forgot.newPassword !== this.form.forgot.confirmPassword) {
          this.showMessageBox('两次输入的密码不一致');
          isValid = false;
        }
      }
      
      return isValid
    },
    
    async handleSubmit() {
      if (!this.validateForm()) return
      
      this.isSubmitting = true
      try {
        if (this.activeTab === 'account') {
          await this.login({
            identifier: this.form.account.identifier,
            password: this.form.account.password
          });
          
          this.showMessageBox('登录成功，正在跳转...', 'success');
          
          const redirectPath = this.$route.query.redirect || '/';
          
          setTimeout(() => {
            this.$router.push(redirectPath);
          }, 1500);
        } else {
          const endpoint = '/auth/reset-password';
          const payload = {
            email: this.form.forgot.email,
            code: this.form.forgot.code,
            newPassword: this.form.forgot.newPassword
          };

          const response = await this.$axios.post(endpoint, payload);
          
          this.showMessageBox('密码重置成功，请使用新密码登录', 'success');
          
          setTimeout(() => {
            this.activeTab = 'account';
            this.form.forgot = {
              email: '',
              code: '',
              newPassword: '',
              confirmPassword: ''
            };
          }, 1500);
        }
      } catch (error) {
        if (error.response) {
          console.error('重置密码错误详情:', error.response.data);
          this.showMessageBox(error.response.data.message || `重置失败：${error.response.status} ${error.response.statusText}`);
        } else if (error.request) {
          console.error('无响应:', error.request);
          this.showMessageBox('服务器未响应，请检查网络连接');
        } else {
          console.error('', error.message);
          this.showMessageBox('' + error.message);
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    
    async handleSendResetCode() {
      if (!this.form.forgot.email) {
        this.showMessageBox('请先输入邮箱')
        return
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.forgot.email)) {
        this.showMessageBox('邮箱格式不正确')
        return
      }

      if (this.countdown > 0) {
        return
      }

      this.isSendingCode = true
      try {
        await this.$axios.post('/auth/send-code', { email: this.form.forgot.email })
        this.showMessageBox('验证码已发送，请查收邮箱', 'success')
        this.startCountdown()
      } catch (error) {
        if (error.response) {
          this.showMessageBox(error.response.data.message || '发送验证码失败')
        } else {
          this.showMessageBox('发送验证码失败，请稍后重试')
        }
      } finally {
        this.isSendingCode = false
      }
    },
    
    startCountdown() {
      this.countdown = 60
      const timer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  position: relative;
  overflow: hidden;
  font-family: 'Orbitron', sans-serif;
}

/* 量子粒子效果 */
.login-container::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: 
    radial-gradient(circle at 50% 50%, rgba(0, 180, 255, 0.1) 0%, transparent 50%),
    linear-gradient(45deg, rgba(0, 247, 255, 0.05) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(255, 0, 255, 0.05) 25%, transparent 25%);
  background-size: 100% 100%, 60px 60px, 60px 60px;
  background-position: center center, 0 0, 0 0;
  pointer-events: none;
  z-index: 0;
}

.login-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 440px;
  padding: 2.5rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.login-title {
  color: #1a1a2e;
  font-size: 1.75rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 2rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tab-container {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.login-tab {
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-weight: 500;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #333333;
}

.login-tab--active {
  background: #0052CC;
  color: white;
  box-shadow: 0 1px 3px 0 rgba(0, 82, 204, 0.1);
}

.login-tab:not(.login-tab--active):hover {
  background: #f8fafc;
  color: #2684FF;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.input-group {
  margin-bottom: 1.5rem;
}

.input-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #4a5568;
  font-size: 0.875rem;
  font-weight: 500;
}

input {
  width: 100%;
  padding: 0.875rem 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #2d3748;
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

input:focus {
  outline: none;
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  background: #ffffff;
}

input::placeholder {
  color: #a0aec0;
}

.input-error {
  border-color: #e53e3e;
  background: #fff5f5;
}

.error-msg {
  color: #e53e3e;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.submit-btn {
  width: 100%;
  padding: 0.875rem;
  background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(66, 153, 225, 0.2);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes shake {
  10%, 90% { transform: translateX(-1px); }
  20%, 80% { transform: translateX(2px); }
  30%, 50%, 70% { transform: translateX(-3px); }
  40%, 60% { transform: translateX(3px); }
}

@media (max-width: 768px) {
  .login-card {
    width: 90%;
    padding: 1.5rem;
  }
}

.verification-code-group {
  display: flex;
  gap: 1rem;
}

.verification-input {
  flex: 1;
}

.send-code-btn {
  padding: 0.875rem 1.5rem;
  background: #4299e1;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  min-width: 120px;
}

.send-code-btn:hover:not(:disabled) {
  background: #3182ce;
  transform: translateY(-1px);
}

.send-code-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background: #a0aec0;
}

.password-input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-group input {
  padding-right: 40px;
}

.toggle-password-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 5px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s ease;
}

.toggle-password-btn:hover {
  color: #4299e1;
}

.toggle-password-btn:focus {
  outline: none;
}

.toggle-password-btn i {
  font-size: 16px;
}
</style>
